import React, { useMemo } from "react";
import { CHART_OVERLAY_BADGE_STYLE } from "./badgeStyles";
import PropTypes from "prop-types";
import { useThemeStore, getThemeColors } from "../../../store/themeStore";
import { isIntradayPeriodicity } from "@chartLib/lib/utils";

/**
 * Calculate price change percentage
 * @param {number} currentPrice - Current close price
 * @param {number} openPrice - Open price of the bar
 * @returns {Object} - { change: number, percentChange: number }
 */
function calculatePriceChange(currentPrice, openPrice) {
  if (!currentPrice || !openPrice || openPrice === 0) {
    return { change: 0, percentChange: 0 };
  }

  const change = currentPrice - openPrice;
  const percentChange = (change / openPrice) * 100;

  return { change, percentChange };
}

/**
 * Format price change for display
 * @param {number} percentChange - Percent change value
 * @returns {string} - Formatted string like "+1.25%" or "-0.50%"
 */
function formatPercentChange(percentChange) {
  const sign = percentChange >= 0 ? "+" : "";
  return `${sign}${percentChange.toFixed(2)}%`;
}

/**
 * Format points change for display
 * @param {number} change - Points change value
 * @returns {string} - Formatted string like "264" or "-150"
 */
function formatPointsChange(change) {
  // Round to reasonable precision based on magnitude
  if (Math.abs(change) >= 1) {
    return Math.round(change).toString();
  }
  return change.toFixed(2);
}

/**
 * Check if countdown timer is visible for this periodicity
 * @param {string} periodicity - Chart periodicity
 * @returns {boolean} - True if countdown is visible
 */
function isCountdownVisible(periodicity) {
  // Countdown only shows for intraday (min and h) — must agree with
  // BarCountdownTimer.shouldShow (leftPosition depends on it)
  return isIntradayPeriodicity(periodicity);
}

/**
 * PriceChangeIndicator - Shows the price variation percentage of the current bar
 *
 * Calculates: (close - open) / open * 100
 *
 * Shows the variation of the current bar (open vs close)
 */
const PriceChangeIndicator = ({ chartData, periodicity, inline = false }) => {
  // Get theme colors
  const theme = useThemeStore((state) => state.theme);
  const themeColors = getThemeColors(theme);
  const isDarkMode = themeColors.isDark;

  // Calculate price change
  const priceChangeData = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Get current bar
    const lastBar = chartData[chartData.length - 1];
    if (!lastBar || !lastBar.close || !lastBar.open) return null;

    const currentPrice = lastBar.close;
    const openPrice = lastBar.open;

    const { change, percentChange } = calculatePriceChange(
      currentPrice,
      openPrice,
    );

    return {
      change,
      percentChange,
      isPositive: percentChange >= 0,
    };
  }, [chartData]);

  // Don't render if no data
  if (!priceChangeData) {
    return null;
  }

  const { change, percentChange, isPositive } = priceChangeData;

  // Colors matching ChartIQ style (same as MACD histogram)
  const textColor = isPositive ? "#8cc176" : "#b82c0c";
  const borderColor = isPositive
    ? "rgba(140, 193, 118, 0.3)"
    : "rgba(184, 44, 12, 0.3)";

  // Format: (▲) 264 (+0.12%)
  const arrow = isPositive ? "▲" : "▼";
  const points = formatPointsChange(Math.abs(change));
  const percent = formatPercentChange(percentChange);

  // Calculate left position based on whether countdown timer is visible (only for non-inline mode)
  const showCountdown = isCountdownVisible(periodicity);
  const leftPosition = showCountdown ? "135px" : "8px";

  return (
    <div
      style={{
        ...(inline
          ? {}
          : {
              position: "absolute",
              bottom: "35px",
              left: leftPosition,
              zIndex: 100,
            }),
        backgroundColor: themeColors.overlayBg,
        color: textColor,
        ...CHART_OVERLAY_BADGE_STYLE,
        pointerEvents: "none",
        boxShadow: isDarkMode
          ? "0 4px 6px rgba(0, 0, 0, 0.3)"
          : "0 2px 4px rgba(0, 0, 0, 0.1)",
        border: `1px solid ${borderColor}`,
      }}
    >
      <span style={{ marginRight: "6px" }}>{arrow}</span>
      <span
        style={{
          color: isDarkMode ? "#ffffff" : "#000000",
          marginRight: "6px",
        }}
      >
        {points}
      </span>
      <span>({percent})</span>
    </div>
  );
};

PriceChangeIndicator.propTypes = {
  chartData: PropTypes.array,
  periodicity: PropTypes.string,
  inline: PropTypes.bool,
};

export default PriceChangeIndicator;
